import { transformDocumentNumber } from '@/components/custom/erp';
import { MA_CHUNG_TU } from '@/constants';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: any[]) => {
  return detailRows.map((row: any, index: number) => ({
    line: index + 1,

    // Account and customer information - follow pattern with fallback
    tk: row.tk_data?.uuid || row.tk || '',
    ma_kh: row.ma_dt_data?.uuid || row.ma_kh || '',

    // Financial amounts - handle both API format and form format
    ps_no_nt: parseFloat(row.ps_no_nt) || parseFloat(row.ps_no) || 0,
    ps_co_nt: parseFloat(row.ps_co_nt) || parseFloat(row.ps_co) || 0,

    // Group and description
    nh_dk: row.nh_dk || '',
    dien_giai: row.dien_giai || '',

    // Organizational information - follow pattern with fallback
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || ''
  }));
};

export const transformTaxRows = (taxRows: any[]) => {
  return taxRows.map((row: any, index: number) => ({
    line: index + 1,
    so_ct0: row.so_hd || '',
    so_ct2: row.so_ct2 || '',
    ngay_ct0: row.ngay_hd || null,
    ma_thue: row.ma_thue || '',
    thue_suat: row.thue_suat || 0,
    ma_mau_ct: row.ma_mau_ct_data?.uuid || '',
    ma_mau_bc: row.ma_mau_bc || '',
    ma_tc_thue: row.ma_tc_thue_data.uuid || '',
    ma_kh: row.ma_ncc_data?.uuid || '',
    ten_kh_thue: row.ma_ncc_data?.customer_name || '',
    dia_chi: row.ma_ncc_data?.address || '',
    ma_so_thue: row.ma_ncc_data?.tax_code || '',
    ten_vt_thue: row.ten_vt_thue || '',
    t_tien_nt: row.t_tien_nt || 0,
    t_tien: row.t_tien || 0,

    tk_thue_no: row.tk_thue_data?.uuid || row.tk_thue_no || '',
    ten_tk_thue_no: row.tk_thue_no_data?.name || row.ten_tk_thue_no || '',
    tk_du: row.tk_du_data?.uuid || row.tk_du || '',
    ten_tk_du: row.tk_du_data?.name || row.ten_tk_du || '',

    t_thue_nt: row.t_thue_nt || 0,
    t_thue: row.t_thue || 0,

    ma_kh9: row.customer_code_data?.uuid || row.ma_kh9 || '',
    ten_kh9: row.customer_code_data?.customer_name || row.ten_kh9 || '',
    ma_tt: row.ma_tt_data?.uuid || row.ma_tt || '',
    ten_tt: row.ma_tt_data?.name || row.ten_tt || '',

    ghi_chu: row.ghi_chu || '',

    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || ''
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param accountRows - Array of account row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: any,
  state: FormFieldState,
  totalPSNo: number,
  totalPSCo: number,
  ...rest: any[]
) => {
  // Transform detail and account rows
  const detail = transformDetailRows(rest[0]);
  const tax = transformTaxRows(rest[1]);

  // Build the final form data object following phieu-xuat-tra-lai-nha-cung-cap pattern
  return {
    ...data,

    // Use state values for fields controlled by state, fallback to form data
    dien_giai: state.dienGiai || data.dien_giai || '',
    ty_gia: state.ty_gia || data.ty_gia || 1,

    ma_kh: state.khachHang?.uuid || '',
    ma_nv: state.nhanVien?.uuid || '',
    tk: state.taiKhoan?.uuid || '',
    ma_tt: state.hanThanhToan?.uuid || '',
    ma_nt: state.ngoaiTe?.uuid || data.ma_nt || '',

    ngay_ct: data.ngay_ct || '',
    ngay_lct: data.ngay_lct || '',

    id_goc: state.id_goc || '',

    status: data.status || '5',

    t_ps_no_nt: totalPSNo,
    t_ps_no: totalPSNo * (data.ty_gia || state.ty_gia || 1),
    t_ps_co_nt: totalPSCo,
    t_ps_co: totalPSCo * (data.ty_gia || state.ty_gia || 1),

    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu, MA_CHUNG_TU.TONG_HOP.BUT_TOAN_HUY_DAO),

    // Detail arrays
    chi_tiet_items: detail,
    thue_items: tax
  };
};
